import { Suspense, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Loader from "./components/GeneralComponents";
import Otp from "./components/Otp/Otp";
import ProfileSelection from "./components/ProfileSelection/ProfileSelection";
import SignIn from "./components/SignIn/SignIn";
import SignUpModal from "./components/SignUp/SignUp";
import TradProfile from "./components/TradsmanProfile/TradsmanProfile";
import UpdateProfile from "./components/UpdateProfile/UpdateProfile";
import UserProfile from "./components/UserProfile/UserProfile";
import Welcome from "./components/Welcome/Welcome";
import Home from "./views/Home/Home";
import ProfileLanding from "./views/ProfileLanding/ProfileLanding";
import { getUserDetails } from "./views/Home/Service";
import PrivacyPolicy from "./views/PrivacyPolicy/PrivacyPolicy";
import SoftwareLicense from "./views/SoftwareLicense/SoftwareLicense";
import TermsOfServices from "./views/TermsOfServices/TermsOfServices";
import EnglishMessages from "./Language/en.json";
import SpanishMessages from "./Language/espanol.json";
import { IntlProvider } from "react-intl";
import PaymentScreenOne from "./views/bankInfoPages/paymentScreenOne";
import PaymentScreenTwo from "./views/bankInfoPages/paymentScreenTwo";
import BankAccountList from "./views/bankInfoPages/bankAccountList";
import PaymentOptions from "./views/bankInfoPages/paymentOptions";
import AddHomAccount from "./views/bankInfoPages/addHomAccount";
import EarningSpendingInfo from "./views/earningSpendingInfoPage/earningSpendingInfo";
import Wallet from "./views/wallet/wallet";
import Settings from "./views/settings/Settings";
import ShareApp from "./views/ShareApp/ShareApp.jsx";
import Chat from "./views/chat/Chat";
import DepositInstructions from "./views/DepositInstructions/DepositInstructions";
import { W9from } from "./views/W9Form/W9from";
import FeeAgreement from "./views/FeeAgreement/FeeAgreement";
import ContractListing from "./views/Contracts/ContractListing.js";
import ContractDetails from "./views/Contracts/contractDetails.js";
import SubmitReview from "./views/SubmitReview/SubmitReview";
import { CreateContractScreen } from "./views/Contracts/createContract.js";
import SupportPage from "./views/Support/SupportPage.jsx";
import GenerateReceiptsAndInvoicesScreen from "./views/ReceiptsAndInvoicesScreen/GenerateReceiptsAndInvoicesScreen.js";
import CreateInvoiceAndReceiptScreen from "./views/ReceiptsAndInvoicesScreen/CreateInvoiceAndReceiptScreen.js";
import SendEstimationswithOptions from "./views/Contracts/SendEstimationswithOptions.js";
import { SocketConnection } from "./sockets/SocketConnection";
import { messaging } from "./utils/firebase.js";
import { onMessage } from "firebase/messaging";
import { message } from "antd";
import { FeedsListing } from "./views/Feeds/FeedsListing.js";
import CreatePost from "./views/Feeds/CreatePost.js";
import { PostDetails } from "./views/Feeds/PostDetails.js";
import SendOffer from "./views/Feeds/SendOffer.js";
import { HowToUseApp } from "./views/HowToUseApp/HowToUseApp.js";
import { Map } from "./views/Maps/Map.js";
import { UploadStories } from "./components/Stories/UploadStories";
import UploadProfileVideos from "./views/Profile/UploadProfileVideos.js";
import { Subscription } from "./views/Subscription/Subscription.js";
import ChangeUsername from "./views/settings/ChangeUsername.js";
import { OtpVerification } from "./views/settings/OtpVerification.js";
import StripeAddCard from "./views/StripeAddCard/StripeAddCard.js";
import StripeOnboarding from "./views/StripeOnboarding/StripeOnboarding.js";
import StripeRefresh from "./views/StripeOnboarding/StripeRefresh.js";
import TaxDocuments from "./views/TaxDocuments/TaxDocuments.js";
import { StripeRouteGuard } from "./components/Stripe/StripeRouteGuard.js";
import { ROUTES } from "./constants/routes.js";

/**
 * Landing redirect for any unmatched path.
 *
 * Carries the attempted path through as `?next=` instead of discarding it: the
 * protected routes are absent from the table while signed out, so an emailed
 * deep link opened by a logged-out user ends up here, and dropping the URL
 * leaves them no way back to what they clicked.
 */
const RedirectHome = () => {
  const { pathname, search } = useLocation();
  const signedIn = Boolean(localStorage.getItem("data"));

  // Signed in, so the path is simply wrong — the landing screen is the useful
  // place to be. Signed out, it is far more likely a protected route that is
  // absent from the table, so send them to sign-in with the destination kept.
  if (signedIn) {
    return <Navigate to="/" replace />;
  }
  const attempted = `${pathname}${search}`;
  return (
    <Navigate to={`/login?next=${encodeURIComponent(attempted)}`} replace />
  );
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      onMessage(messaging, (payload) => {
        message.success(payload.notification.body);
      });
      await getUserDetails(dispatch);
    };
    fetchData();
  }, []);

  const lang = JSON.parse(localStorage.getItem("lang"));

  let Language;

  if (lang) {
    Language = "es";
  } else {
    Language = "en";
  }

  const connectSockets = async () => {
    const isAuthenticated = localStorage.getItem("data");
    if (isAuthenticated) {
      const token = JSON.parse(isAuthenticated);
      const accessToken = token?.accessToken;
      await SocketConnection(accessToken);
    }
  };

  useEffect(() => {
    connectSockets();
  }, []);

  const messages = {
    es: SpanishMessages,
    en: EnglishMessages,
  };
  const isAuthenticated = localStorage.getItem("data");
  return (
    <IntlProvider
      locale={Language}
      messages={messages[Language]}
      defaultLocale="en"
    >
      <Suspense fallback={<Loader />}>
        <>
          <Routes>
            <Route path="/login" element={<SignIn />} />
            <Route path="/signup" element={<SignUpModal />} />
            <Route path="/profile-selection" element={<ProfileSelection />} />
            <Route path="/verifyOtp" element={<Otp />} />
            <Route path={`/privacy-policy/:lang`} element={<PrivacyPolicy />} />
            <Route
              path={`/deposit-instructions/:lang`}
              element={<DepositInstructions />}
            />

            <Route
              path="/terms-of-services/:lang"
              element={<TermsOfServices />}
            />
            <Route
              path="/software-license/:lang"
              element={<SoftwareLicense />}
            />
            <Route path="/fee-agreement/:lang" element={<FeeAgreement />} />
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/support/:lang" element={<SupportPage />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="how-use-app" element={<HowToUseApp />} />
            <Route path="/profile" element={<ProfileLanding />} />
            {/* Stripe Connect return target. Public on purpose: Stripe redirects
                here from its own hosted flow with a bare URL, and the browser
                that comes back may have no session — a returning user was
                landing on an unmatched route, which React Router renders as a
                blank page with no hint that onboarding even succeeded. */}
            <Route path={ROUTES.STRIPE_RETURN} element={<StripeOnboarding />} />
            {/* Connect refresh target, public for the same reason. Account
                Links are single-use, so Stripe sends anyone holding an expired
                one here — and with no route it fell to the catch-all, which
                bounced a signed-in payee home and looped a signed-out one
                through /login and back. */}
            <Route path={ROUTES.STRIPE_REFRESH} element={<StripeRefresh />} />
            {/* 1099 tax documents. Public for the same reason `subscription` is
                (line 159): mobile opens this in the system browser with a
                `?token=` deep link and no localStorage session, so a guarded
                route would not exist in the table and it would fall to the
                catch-all. The page requires an identity of its own and shows a
                sign-in prompt without one. Stripe's pre-filing emails link
                here too, and that browser may carry no session either. */}
            <Route
              path={ROUTES.STRIPE_TAX_DOCUMENTS}
              element={<TaxDocuments />}
            />
            {isAuthenticated && (
              <>
                <Route path="/submit-review" element={<SubmitReview />} />
                <Route path={`/trads-profile`} element={<TradProfile />} />
                <Route path={`/share-app`} element={<ShareApp />} />
                <Route
                  path={`/generate-invoices-or-receipts`}
                  element={<GenerateReceiptsAndInvoicesScreen />}
                />
                <Route
                  path={`/create-invoices-or-receipts`}
                  element={<CreateInvoiceAndReceiptScreen />}
                />
                <Route path="/user-profile" element={<UserProfile />} />
                <Route path="/update-user" element={<UpdateProfile />} />
                {/* Legacy raw-PII / AeroSync bank screens: under Stripe they
                    redirect to the Stripe equivalent so the user_password / raw
                    SSN+bank paths are unreachable regardless of entry point. */}
                <Route
                  path="/get-payments"
                  element={
                    <StripeRouteGuard
                      element={<PaymentScreenOne />}
                      redirectTo={ROUTES.STRIPE_ONBOARDING}
                    />
                  }
                />
                <Route
                  path="/bank-info"
                  element={
                    <StripeRouteGuard
                      element={<PaymentScreenTwo />}
                      redirectTo={ROUTES.STRIPE_ONBOARDING}
                    />
                  }
                />
                <Route
                  path="/bank-accounts"
                  element={
                    <StripeRouteGuard
                      element={<BankAccountList />}
                      redirectTo={ROUTES.STRIPE_ADD_CARD}
                    />
                  }
                />
                <Route path="/payment-options" element={<PaymentOptions />} />
                <Route
                  path="/add-hom-account"
                  element={
                    <StripeRouteGuard
                      element={<AddHomAccount />}
                      redirectTo={ROUTES.STRIPE_ADD_CARD}
                    />
                  }
                />
                <Route
                  path="/spendings-earnings"
                  element={<EarningSpendingInfo />}
                />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/chat" element={<Chat />} />
                {/* Guarded for the same reason as the bank screens above, and
                    missed when they were: this is the legacy Everee/GigWage W-9,
                    a raw-PII tax form. Under Stripe the tax surface is the
                    Connect account-management page, which collects the TIN on
                    Stripe's domain — two tax-ID surfaces is one too many, and
                    this one posts to a retired payroll vendor. Under aeropay the
                    legacy form still renders, unchanged. */}
                <Route
                  path="/view-w9-form"
                  element={
                    <StripeRouteGuard
                      element={<W9from />}
                      redirectTo={ROUTES.STRIPE_TAX_DOCUMENTS}
                    />
                  }
                />
                <Route path="/contract-lists" element={<ContractListing />} />
                <Route path="/contract-details" element={<ContractDetails />} />
                <Route
                  path="/create-contract"
                  element={<CreateContractScreen />}
                />
                <Route
                  path="/send-estimations"
                  element={<SendEstimationswithOptions />}
                />
                <Route path="feeds" element={<FeedsListing />} />
                <Route path="create-post" element={<CreatePost />} />
                <Route path="post-details" element={<PostDetails />} />
                <Route path="send-offer" element={<SendOffer />} />
                <Route path="gigmi-maps" element={<Map />} />
                <Route path="upload-gigs" element={<UploadStories />} />
                <Route path="upload-videos" element={<UploadProfileVideos />} />
                <Route path="/change-username" element={<ChangeUsername />} />
                <Route path="/otp-verification" element={<OtpVerification />} />
                <Route
                  path={ROUTES.STRIPE_ADD_CARD}
                  element={<StripeAddCard />}
                />
                <Route
                  path={ROUTES.STRIPE_ONBOARDING}
                  element={<StripeOnboarding />}
                />
              </>
            )}
            {/* Without this an unmatched path renders nothing at all — a blank
                white page. Send those to the landing screen instead, which is
                reachable signed in or out.

                The protected routes above do not exist in the table while
                signed out, so an emailed deep link lands here too. The path is
                carried through as `?next=` rather than dropped, so it is at
                least recoverable — wiring sign-in to consume it means touching
                the auth flow, which belongs in its own change. */}
            <Route path="*" element={<RedirectHome />} />
          </Routes>
        </>
      </Suspense>
    </IntlProvider>
  );
}

export default App;
